import { yupResolver } from "@hookform/resolvers/yup";
import VerticalForm from "../../components/forms/VerticalForm";
import LoginContainerLayout from "../login/LoginContainerLayout";
import * as yup from "yup";
import { Alert, Button } from "react-bootstrap";
import { CiUser, CiLock } from "react-icons/ci";
import { Link, useNavigate, useSearchParams } from "react-router";
import LoginInputField from "../../components/LoginInputField";
import {
  useCreateUserWithInviteMutation,
  useGetTeamInvitePreviewQuery,
} from "../../features/users/usersApi";
import { toast } from "react-toastify";
import FullScreenLoader from "../../components/FullScreenLoader";

interface JoinForm {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const JoinTeam = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() ?? "";
  const navigate = useNavigate();

  const { data: preview, isFetching, isError, error } = useGetTeamInvitePreviewQuery(
    token,
    { skip: !token }
  );

  const [createUser, { isLoading: isSubmitting }] = useCreateUserWithInviteMutation();

  const schemaResolver = yupResolver(
    yup.object().shape({
      first_name: yup.string().required("First name is required"),
      last_name: yup.string().default(""),
      email: yup.string().email().required(),
      password: yup
        .string()
        .min(8, "Password must be at least 8 characters")
        .required("Password is required"),
      confirmPassword: yup
        .string()
        .oneOf([yup.ref("password")], "Passwords must match")
        .required("Confirm your password"),
    })
  );

  if (!token) {
    return (
      <LoginContainerLayout
        hideSocialLogin
        title="Invalid invite"
        subtitle="This link is missing a token. Ask your admin for a new invitation."
      >
        <Alert variant="warning">
          Open the invite link from your email, or request a new team invite.
        </Alert>
        <Link to="/login">Back to sign in</Link>
      </LoginContainerLayout>
    );
  }

  if (isFetching) {
    return <FullScreenLoader />;
  }

  if (isError || !preview?.data) {
    const errMsg =
      (error as any)?.data?.message ??
      (typeof (error as any)?.data === "string" ? (error as any).data : null) ??
      "This invitation is invalid or has expired.";
    return (
      <LoginContainerLayout
        hideSocialLogin
        title="Invitation unavailable"
        subtitle="The link may have expired or already been used."
      >
        <Alert variant="danger">{errMsg}</Alert>
        <Link to="/login">Back to sign in</Link>
      </LoginContainerLayout>
    );
  }

  const inviteEmail = preview.data.email;
  const orgName = preview.data.organization_name;

  const onSubmit = async (formData: JoinForm) => {
    if (!token) {
      toast.error("Missing invite link");
      return;
    }
    try {
      await createUser({
        first_name: formData.first_name.trim(),
        last_name: formData.last_name?.trim() || undefined,
        email: inviteEmail.trim().toLowerCase(),
        password: formData.password,
        invite_token: token,
      }).unwrap();
      toast.success("Account created. You can sign in now.");
      navigate("/login");
    } catch (e: any) {
      const msg =
        e?.data?.message ??
        (typeof e?.data === "string" ? e.data : null) ??
        "Could not create account";
      toast.error(msg);
    }
  };

  return (
    <LoginContainerLayout
      hideSocialLogin
      title="Join your team"
      subtitle={
        orgName
          ? `Create your Memorique account for ${orgName}`
          : "Create your Memorique account"
      }
      helpText="Use the same email address the invitation was sent to."
    >
      <VerticalForm<JoinForm>
        onSubmit={onSubmit}
        resolver={schemaResolver}
        defaultValues={{
          first_name: preview.data.first_name ?? "",
          last_name: preview.data.last_name ?? "",
          email: inviteEmail,
          password: "",
          confirmPassword: "",
        }}
      >
        <LoginInputField
          label="First name"
          type="text"
          name="first_name"
          placeholder="First name"
          icon={<CiUser size={18} />}
          containerClass="mb-2"
        />
        <LoginInputField
          label="Last name (optional)"
          type="text"
          name="last_name"
          placeholder="Last name"
          icon={<CiUser size={18} />}
          containerClass="mb-2"
        />
        <LoginInputField
          label="Email"
          type="email"
          name="email"
          placeholder="you@company.com"
          icon={<CiUser size={18} />}
          containerClass="mb-2"
          readOnly
        />
        <LoginInputField
          label="Password"
          type="password"
          name="password"
          placeholder="At least 8 characters"
          icon={<CiLock size={18} />}
          containerClass="mb-2"
        />
        <LoginInputField
          label="Confirm password"
          type="password"
          name="confirmPassword"
          placeholder="Confirm password"
          icon={<CiLock size={18} />}
          containerClass="mb-2"
        />

        <div className="d-grid gap-2 mt-3">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Creating account…" : "Create account"}
          </Button>
          <Link to="/login" className="text-center small">
            Already have an account? Sign in
          </Link>
        </div>
      </VerticalForm>
    </LoginContainerLayout>
  );
};

export default JoinTeam;
